import { ref, computed } from "vue";
import { defineStore } from "pinia";
import AuthService from "@/services/AuthService";
import router from "@/router";
import { useReportStore } from "@/stores/reports";
import { useDashboardStore } from "@/stores/dashboards";
import { useNavigationStore } from "@/stores/navigation";
import Bugsnag from "@bugsnag/js";
import { tokenService } from "@/services/tokenService";
import { isInactive, resetActivity, clearActivity } from "@/utils/activityTracker";

// Module-level timer so it survives reactive resets without triggering watchers.
let _proactiveRefreshTimer = null;

// Shared in-flight promise so concurrent callers (proactive timer + 401
// interceptor) share a single refresh attempt instead of racing each other.
let _refreshPromise = null;

export const useAuthUserStore = defineStore('auth-user', () => {

    // Stores
    const navigationStore = useNavigationStore();
    const reportStore = useReportStore();
    const dashboardStore = useDashboardStore();

    // State
    // user shape: { id, name, email, role, tenants: [{ id, name, display_name, domain }] }
    const user = ref(null);
    // tenantId is persisted to sessionStorage so it survives a page refresh within
    // the same tab. A new tab starts fresh (token refresh via cookie handles the token,
    // but the user's tenant selection is tab-scoped).
    const tenantId = ref(sessionStorage.getItem('tenant_id') ?? null);
    const cubeToken = ref(null);
    const loading = ref(false);
    const error = ref(null);

    // Getters
    const getAuthenticatedUser = computed(() => user.value);
    const getLoggedIn = computed(() => !!user.value);
    const getTenants = computed(() => user.value?.tenants ?? []);
    const getTenantId = computed(() => tenantId.value);
    const getCurrentTenant = computed(() => getTenants.value.find(t => t.id === tenantId.value) ?? null);
    const getCompanyId = computed(() => tenantId.value);   // alias for backward compat
    const getCompanyName = computed(() => getCurrentTenant.value?.name ?? null);

    // Role getters — derived from user.role.slug (role is an object: { slug, ... })
    // Expected slugs: 'admin' | 'curator' | 'analyst' | 'project-manager' | 'standard-user'
    const getIsAdmin = computed(() => user.value?.role?.slug === 'admin');
    const getIsCurator = computed(() => user.value?.role?.slug === 'curator');
    const getIsAnalyst = computed(() => user.value?.role?.slug === 'analyst');
    const getIsProjectManager = computed(() => user.value?.role?.slug === 'project-manager');
    const getIsStandardUser = computed(() => user.value ? user.value.role?.slug === 'standard-user' : true);
    const getUserRole = computed(() => user.value?.role?.slug ?? 'standard-user');

    const getCubejsToken = computed(() => cubeToken.value);
    const getError = computed(() => error);
    const getLoading = computed(() => loading);
    const getGuest = computed(() => {
        const item = window.localStorage.getItem("guest");
        return item === "isGuest";
    });

    // Schedules a token refresh 60 seconds before the JWT expires.
    // Called whenever a new access token is stored so the session stays alive
    // without requiring a page reload or waiting for a 401.
    function scheduleTokenRefresh(token) {
        clearTimeout(_proactiveRefreshTimer);
        const expiry = tokenService.getTokenExpiry(token);
        if (!expiry) return;
        const delay = expiry - Date.now() - 60_000;
        if (delay <= 0) {
            silentRefresh();
            return;
        }
        _proactiveRefreshTimer = setTimeout(silentRefresh, delay);
    }

    // Single source of truth for refreshing the session. Concurrent callers
    // share the in-flight promise so the proactive timer and the 401 interceptor
    // cannot race each other.
    //
    // Rejects ONLY when the access-token refresh itself fails — that is the
    // definitive "session is dead" signal. Tenant and cube token failures are
    // reported to Bugsnag and swallowed; a transient downstream issue should
    // not log the user out.
    function refreshSession() {
        if (_refreshPromise) return _refreshPromise;
        _refreshPromise = (async () => {
            const response = await AuthService.refreshToken();
            tokenService.setToken(response.data.access_token);
            scheduleTokenRefresh(response.data.access_token);

            if (tenantId.value && user.value) {
                try {
                    await fetchTenantToken({ tenant_id: tenantId.value });
                } catch (err) {
                    Bugsnag.notify(err);
                }
                try {
                    await fetchCubejsToken({
                        user_id: user.value.id,
                        tenant_id: tenantId.value,
                    });
                } catch (err) {
                    Bugsnag.notify(err);
                }
            }
        })().finally(() => { _refreshPromise = null; });
        return _refreshPromise;
    }

    // Best-effort proactive refresh. Gated on inactivity, swallows failures
    // because the 401 interceptor is the source of truth for "session is dead".
    async function silentRefresh() {
        if (!user.value) return;
        if (isInactive()) {
            logout();
            return;
        }
        try {
            await refreshSession();
        } catch {
            // Swallow — next API call's 401 will trigger the real refresh path
            // and force a logout there if the session is truly dead.
        }
    }

    // Actions
    async function login(payload) {
        loading.value = true;
        try {
            const response = await AuthService.login(payload);
            tokenService.setToken(response.data.access_token);
            scheduleTokenRefresh(response.data.access_token);
            resetActivity();
            setGuest('isNotGuest');
            await fetchAuthUser();

            // For single-tenant standard users the tenant is auto-selected;
            // fetch the tenant token first, then the CubeJS token.
            if (tenantId.value) {
                await fetchTenantToken({ tenant_id: tenantId.value });
                await fetchCubejsToken({
                    user_id: user.value.id,
                    tenant_id: tenantId.value,
                });
            }
        } finally {
            loading.value = false;
        }
    }

    function logout() {
        return AuthService.logout()
            .then(() => {
                $reset();
                reportStore.$reset();
                dashboardStore.$reset();
                navigationStore.$reset();

                sessionStorage.clear();
                setGuest("isGuest");
                if (router.currentRoute.name !== "login")
                    router.push({ path: "/login" });
            })
            .catch((err) => {
                error.value = err;
            });
    }

    async function fetchAuthUser() {
        loading.value = true;
        try {
            const response = await AuthService.getAuthUser();
            user.value = response.data;

            // Auto-select the first tenant for standard users
            if (user.value.role?.slug === 'standard-user' && user.value.tenants?.length) {
                setTenantId(user.value.tenants[0].id);
            }

            setGuest('isNotGuest');
            Bugsnag.setUser(user.value.id, user.value.email, user.value.name);
            return response.data;
        } catch (err) {
            user.value = null;
            error.value = err;
        } finally {
            loading.value = false;
        }
    }

    async function fetchCubejsToken(payload) {
        try {
            const response = await AuthService.getCubejsToken(payload);
            cubeToken.value = response.data;
            return response.data;
        } catch (err) {
            error.value = err;
        }
    }

    async function fetchTenantToken(payload) {
        const response = await AuthService.getTenantToken(payload);
        tokenService.setTenantToken(response.data);
        return response.data;
    }

    function setGuest(value) {
        window.localStorage.setItem("guest", value);
    }

    function setTenantId(id) {
        tenantId.value = id;
        if (id) sessionStorage.setItem('tenant_id', id);
        else sessionStorage.removeItem('tenant_id');
    }

    // Backward-compat wrappers — prefer setTenantId directly
    function setCompanyId(id) {
        setTenantId(id);
    }

    function setCompanyName(_name) {
        // no-op: company name is now derived from getCurrentTenant
    }

    // Attempt to restore a session on page refresh by calling the refresh endpoint.
    // The backend's HttpOnly refresh token cookie is sent automatically (withCredentials).
    // Returns true if the session was restored, false if the user must log in again.
    //
    // Refuses to restore when the user has been idle past the inactivity limit
    // — even if the refresh-token cookie is still valid, the previous session
    // had no recent activity and we want to force a fresh login.
    async function bootstrap() {
        if (isInactive()) {
            clearActivity();
            return false;
        }
        try {
            const response = await AuthService.refreshToken();
            tokenService.setToken(response.data.access_token);
            scheduleTokenRefresh(response.data.access_token);
            await fetchAuthUser();

            if (tenantId.value) {
                try {
                    await fetchTenantToken({ tenant_id: tenantId.value });
                    await fetchCubejsToken({
                        user_id: user.value.id,
                        tenant_id: tenantId.value,
                    });
                } catch {
                    // Token fetch failed (e.g. backend not yet ready).
                    // The session itself is still valid; the router guard will
                    // redirect to select-company so the user can try again.
                }
            }

            return true;
        } catch {
            return false;
        }
    }

    // Patches the in-memory user if the updated record matches the logged-in user.
    // Keeps auth.getTenants in sync after a user edits their own tenants without
    // requiring a full re-fetch from the server.
    // If the active tenant was removed from the updated tenant list, tenant-scoped
    // auth state is cleared so the UI and API tokens cannot disagree.
    function patchAuthUser(updatedUser) {
        if (user.value?.id === updatedUser.id) {
            const roleChanged = updatedUser.role?.slug !== user.value.role?.slug;

            user.value = { ...user.value, ...updatedUser };

            const hasTenantList = Array.isArray(updatedUser.tenants);
            const tenantStillValid = hasTenantList && updatedUser.tenants.some(
                (tenant) => String(tenant.id) === String(tenantId.value)
            );

            // If we have a real tenant list and there is an active tenant selected and that tenant is no longer in the list
            // clear the tenant session and tokens to avoid mismatched state
            // where the UI thinks we're scoped to a tenant but the backend rejects the token.
            if (hasTenantList && tenantId.value && !tenantStillValid) {
                setTenantId(null);
                cubeToken.value = null;
                tokenService.clearTenantToken();
            }

            // Re-filter the nav tree so visible items reflect the new role immediately.
            // Called after the user patch so getUserRole already returns the new slug.
            if (roleChanged) {
                navigationStore.setNodes();
            }
        }
    }

    function $reset() {
        clearTimeout(_proactiveRefreshTimer);
        _proactiveRefreshTimer = null;
        user.value = null;
        cubeToken.value = null;
        error.value = null;
        loading.value = false;
        tokenService.clearToken();
        tokenService.clearTenantToken();
        setTenantId(null);
        clearActivity();
    }

    return {
        getAuthenticatedUser,
        getLoggedIn,
        getTenants,
        getTenantId,
        getCurrentTenant,
        getCompanyId,
        getCompanyName,
        getIsAdmin,
        getIsCurator,
        getIsAnalyst,
        getIsProjectManager,
        getIsStandardUser,
        getUserRole,
        getCubejsToken,
        getError,
        getLoading,
        getGuest,
        login,
        logout,
        bootstrap,
        scheduleTokenRefresh,
        refreshSession,
        silentRefresh,
        fetchAuthUser,
        fetchCubejsToken,
        fetchTenantToken,
        setTenantId,
        setCompanyId,
        setCompanyName,
        patchAuthUser,
        $reset,
    };
});
